import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './styles.module.scss';
import { Box, makeStyles, TextField } from '@material-ui/core';
import { Fragment, MouseEventHandler, ReactNode, useEffect, useRef, useState } from 'react';
import themes from 'styles/themes';
import { ReactComponent as CloseIconBlack } from '../../assets/svg/close-black.svg';
import { DEFAULT_FEEDBACK_OPTION, Option, Options } from './types';
import { Spinner } from 'react-bootstrap';
import { TextErr } from 'component/customize_style_component/styled_title';
import { ReactComponent as CheckCircle } from '../../assets/svg/check-circle.svg';
import { ReactComponent as CheckBlankCircle } from '../../assets/svg/checkbox_blank_ circle_line.svg';
import { ReactComponent as CheckCircleFill } from '../../assets/svg/checkbox_circle_fill.svg';
import { ReactComponent as ArrowUp } from '../../assets/svg/arrow_up_s_line.svg';
import { ReactComponent as ArrowDown } from '../../assets/svg/arrow_down_s_line.svg';
import { STRINGS } from 'constants/strings';
import { FeedbackReason } from 'graph_ql/feedback/types';
import { flattenFeedbackOptions } from 'utils/flattenFeedbackOptions';
import { Collapse } from 'react-collapse';
import clsx from 'clsx';

interface PopupButton {
    label: string;
    onClick: MouseEventHandler<HTMLButtonElement>;
}

interface PopupProps {
    show: boolean;
    onClose: MouseEventHandler<HTMLDivElement>;
    title: ReactNode;
    options: Option[];
    selectedOptions: Options;
    setSelectedOptions: React.Dispatch<React.SetStateAction<Options>>;
    loading: boolean;
    customValueError: boolean;
    setCustomValueError: React.Dispatch<React.SetStateAction<boolean>>;
    handleSubmit: MouseEventHandler<HTMLButtonElement>;
    setHidePopup: any;
    shouldShowSuccess?: boolean;
    successMessage: string;
    successDetails: string;
    submitLoading: boolean;
    inReview?: boolean;
    customButtons?: PopupButton[];
    displayOptionsWithoutCollapsible?: boolean;
}

const useStyles = makeStyles(() => ({
    title: {
        width: '100%',
        textAlign: 'center',
        color: themes.palette.primary.main,
        fontSize: '18px',
        margin: 0,
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        fontFamily: 'Fraunces',
        flex: 1,
    },
    closeButton: {
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
    },
    category: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '18px',
        paddingBottom: '18px',
        '&:not(:last-child)': {
            borderBottom: '1px solid #D1D1D1',
        },
    },
    listOptions: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    simpleReviewListOptions: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    option: {
        border: '1px solid #D1D1D1',
        padding: '10px 18px',
        borderRadius: '42px',
        marginLeft: '5px',
        marginRight: '5px',
        marginTop: '8px',
        fontSize: '14px',
        fontWeight: 400,
        fontFamily: 'PoppinsRegular',
        textAlign: 'center',
    },
    optionContainer: {
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        borderRadius: '16px',
        alignSelf: 'stretch',
        padding: '12px 4px',
    },
    categoryName: {
        fontFamily: 'PoppinsMedium',
        fontSize: '16px',
        marginBottom: '8px',
    },
    collapseCategoryNameGroup: {
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
    },
    collapseCategoryName: {
        marginLeft: 8,
        marginBottom: '0 !important',
        flex: 1,
    },
    customTextField: {
        marginTop: 10,
        width: '100%',
        '& .MuiInputBase-root': {
            fontFamily: 'PoppinsRegular',
        },
        '& .MuiInput-underline': {
            '&.Mui-focused': {
                bottom: 0,
                borderBottom: 'none',
            },
            '&:before': {
                borderBottom: `1px solid ${themes.palette.grey['300']} !important`,
            },
            '&:after': {
                borderBottom: 'none',
            },
        },
    },
    submitButton: {
        textTransform: 'uppercase',
        width: '100%',
        height: '48px',
        padding: '15px 0',
        color: 'white',
        marginTop: 20,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'PoppinsRegular',
        background: themes.palette.primary.main,
        border: 'none',
        borderRadius: '29px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    reviewButton: {
        width: '100%',
        height: 48,
        padding: '5px 0',
        color: themes.palette.primary.main,
        marginTop: 5,
        fontSize: 14,
        fontWeight: 400,
        fontFamily: 'PoppinsRegular',
        background: '#fff',
        border: '1px solid ',
        borderRadius: 29,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    successMessageContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
    successMessage: {
        marginTop: 10,
        fontFamily: 'PoppinsMedium',
    },
    successDetails: {
        marginTop: 10,
        fontFamily: 'PoppinsRegular',
    },
    guideText: {
        fontFamily: 'PoppinsRegular',
        textAlign: 'center',
        marginBottom: 10,
        fontSize: 12,
    },
}));

const MultiSelectPopup = ({
    show,
    onClose,
    title,
    options,
    selectedOptions,
    setSelectedOptions,
    loading,
    customValueError,
    setCustomValueError,
    handleSubmit,
    setHidePopup,
    shouldShowSuccess,
    successMessage,
    successDetails,
    submitLoading,
    inReview,
    customButtons,
    displayOptionsWithoutCollapsible,
}: PopupProps) => {
    const classes = useStyles();
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
    const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>([]);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const textField = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const defaultSelectedCategoryIds = selectedOptions.options.length
            ? selectedOptions.options.reduce((list: string[], option) => {
                if (option.category?.title) {
                    list.push(option.category.title);
                }
                return list;
            }, [])
            : [];
        const defaultExpandedCategoryIds = selectedOptions.options.length
            ? selectedOptions.options.reduce((list: string[], option) => {
                if (option.category?.title) {
                    list.push(option.category.title);
                }
                return list;
            }, [])
            : [];
        setSelectedCategoryIds(defaultSelectedCategoryIds);
        // if (defaultExpandedCategoryIds.length) {
        setExpandedCategoryIds(defaultExpandedCategoryIds);
        // }
    }, [selectedOptions]);

    const toggleSelectOption = (option: Option, category: any) => {
        const index = selectedOptions?.options?.findIndex((selected) => selected?.id === option?.id);
        // If option is already selected, remove it
        if (index !== -1) {
            const newResults = [...selectedOptions?.options!];
            newResults?.splice(index!, 1);
            setSelectedOptions({
                ...selectedOptions,
                options: newResults,
            });
            // check if the category has no selected option -> unselect the category
            if (inReview && !newResults.some((opt) => opt.category?.title === category.category)) {
                setSelectedCategoryIds((prevState) => prevState.filter((c) => c !== category.category));
            }
        }
        // If option not selected yet, add it
        else {
            const removedAlignedOptions = selectedOptions?.options?.reduce((acc: Option[], op: Option) => {
                if (
                    option.id !== op?.id &&
                    op?.category?.id === option?.category?.id &&
                    op?.displayOrderWithInCategory === option?.displayOrderWithInCategory
                ) {
                    return acc;
                }

                return [...acc, op];
            }, []);

            setSelectedOptions({
                ...selectedOptions,
                options: [...removedAlignedOptions, option],
            });
            // check if the category has not been selected -> select the category
            if (inReview && !selectedCategoryIds.includes(category.category)) {
                setSelectedCategoryIds((prevState) => [...prevState, category.category]);
            }
        }
    };

    const handleClose = (e) => {
        onClose(e);
        setShowSuccess(false);
    };

    const handleTextFieldChange = (e) => {
        setCustomValueError(false);
        setSelectedOptions({
            ...selectedOptions,
            customValue: e.target.value,
        });
    };

    const handleMultiSubmit = async (e) => {
        try {
            await handleSubmit(e);
            const hasCustomFeedback = selectedOptions?.options?.find((op) => op?.value === 'Other') !== undefined;
            if (!selectedOptions?.customValue?.length && hasCustomFeedback) {
                setShowSuccess(false);
                if (textField?.current) (textField.current as HTMLElement).scrollIntoView({ behavior: 'smooth' });
            } else {
                setShowSuccess(typeof shouldShowSuccess === 'boolean' ? shouldShowSuccess : true);
                // clear selected options
                setSelectedCategoryIds([]);
                setSelectedOptions(DEFAULT_FEEDBACK_OPTION);
                if (typeof shouldShowSuccess === 'boolean') {
                    setShowSuccess(shouldShowSuccess);
                    handleReviewMore();
                } else {
                    setShowSuccess(true);
                }
            }
        } catch (error) {
            console.error('Error: ', error);
        }
    };

    const handleReviewMore = () => {
        setHidePopup(false);
        setShowSuccess(false);
    };

    const renderOptions = (category: any) => {
        return (
            <div className={displayOptionsWithoutCollapsible ? classes.simpleReviewListOptions : classes.listOptions}>
                {category.options
                    .sort((a, b) => a.displayOrderWithInCategory - b.displayOrderWithInCategory)
                    .map((option, index) => (
                        <Fragment key={option?.id}>
                            {index > 0 &&
                                option.displayOrderWithInCategory -
                                    category.options[index - 1]?.displayOrderWithInCategory >=
                                    1 && <Box width={'100%'} />}
                            <div
                                key={option?.id}
                                className={classes.option}
                                style={{
                                    background: selectedOptions?.options?.some(
                                        (selectedOption) => selectedOption?.id === option?.id,
                                    )
                                        ? '#F2D4BD'
                                        : '#ffffff',
                                    border: selectedOptions?.options?.some(
                                        (selectedOption) => selectedOption?.id === option?.id,
                                    )
                                        ? '1px solid #F2D4BD'
                                        : '1px solid #D1D1D1',
                                }}
                                onClick={() => toggleSelectOption(option, category)}
                            >
                                {option.title}
                            </div>
                        </Fragment>
                    ))}
            </div>
        );
    };

    return (
        <Modal
            className={styles['modal-dialog']}
            show={show}
            aria-labelledby="contained-modal-title-vcenter"
            centered
            contentClassName={
                displayOptionsWithoutCollapsible ? styles['modal-content-simple-review'] : styles['modal-content']
            }
        >
            <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                <h5 className={classes.title}>{title}</h5>
                <div onClick={handleClose} className={classes.closeButton}>
                    <CloseIconBlack />
                </div>
            </Box>
            {showSuccess && (
                <div className={classes.successMessageContainer}>
                    <CheckCircle />
                    <p className={classes.successMessage}>{successMessage}</p>
                    <p className={classes.successDetails} style={{ textAlign: 'center' }}>
                        {successDetails}
                    </p>
                </div>
            )}
            {loading && (
                <div
                    className={`d-flex w-100 justify-content-center`}
                    style={{ marginTop: 'auto', marginBottom: 'auto' }}
                >
                    <Spinner animation="border" style={{ marginLeft: 10, width: 18, height: 18 }} />
                </div>
            )}
            {!loading && !showSuccess && (
                <Box className={classes.optionContainer}>
                    <div className={classes.guideText}>{inReview ? '' : STRINGS.FEEDBACK_TITLE}</div>
                    <Box display={'flex'} flexDirection={'column'}>
                        {flattenFeedbackOptions(options as FeedbackReason[])?.map((category: any, idx) => (
                            <div
                                className={classes.category}
                                style={{ marginBottom: displayOptionsWithoutCollapsible ? 0 : '18px' }}
                                key={`category_${idx}`}
                            >
                                {inReview && !displayOptionsWithoutCollapsible ? (
                                    <div className={classes.collapseCategoryNameGroup}>
                                        {selectedCategoryIds.includes(category.category) ? (
                                            <CheckCircleFill
                                                onClick={() => {
                                                    setSelectedCategoryIds((prevState) =>
                                                        prevState.filter((c) => c !== category.category),
                                                    );
                                                    setSelectedOptions((prevState) => {
                                                        return {
                                                            ...prevState,
                                                            options: prevState.options.filter(
                                                                (op) => op?.category?.title !== category.category,
                                                            ),
                                                        };
                                                    });
                                                }}
                                            />
                                        ) : (
                                            <CheckBlankCircle />
                                        )}
                                        <span
                                            className={clsx(
                                                classes.categoryName,
                                                inReview ? classes.collapseCategoryName : null,
                                            )}
                                        >
                                            {category.category}
                                        </span>
                                        {expandedCategoryIds.includes(category.category) ? (
                                            <ArrowUp
                                                onClick={() => {
                                                    setExpandedCategoryIds((prevState) =>
                                                        prevState.filter((c) => c !== category.category),
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <ArrowDown
                                                onClick={() => {
                                                    setExpandedCategoryIds((prevState) => [
                                                        ...prevState,
                                                        category.category,
                                                    ]);
                                                }}
                                            />
                                        )}
                                    </div>
                                ) : displayOptionsWithoutCollapsible ? (
                                    <></>
                                ) : (
                                    <p className={classes.categoryName}>{category.category}</p>
                                )}

                                {inReview && !displayOptionsWithoutCollapsible ? (
                                    <Collapse isOpened={expandedCategoryIds.includes(category.category)}>
                                        {renderOptions(category)}
                                    </Collapse>
                                ) : (
                                    renderOptions(category)
                                )}
                            </div>
                        ))}
                    </Box>

                    {selectedOptions?.options?.filter((option) => option?.value === 'Other')?.length ? (
                        <div className={classes.customTextField}>
                            <TextField
                                style={{
                                    width: '100%',
                                    fontSize: 16,
                                }}
                                ref={textField}
                                value={selectedOptions?.customValue}
                                multiline
                                maxRows={4}
                                required
                                placeholder={STRINGS.WHAT_MISSING}
                                onChange={handleTextFieldChange}
                            />
                            {customValueError && (
                                <TextErr style={{ display: 'block' }}>{STRINGS.REQUIRED_FIELD}</TextErr>
                            )}
                        </div>
                    ) : null}
                    <button
                        onClick={handleMultiSubmit}
                        disabled={!selectedOptions?.options?.length}
                        className={classes.submitButton}
                    >
                        {STRINGS.SUBMIT}
                        {submitLoading && (
                            <Spinner animation="border" style={{ marginLeft: 8, width: 15, height: 15 }} />
                        )}
                    </button>
                </Box>
            )}
            {showSuccess &&
                (customButtons && customButtons.length ? (
                    <>
                        {customButtons.map((button, btnIndex) => (
                            <button
                                disabled={submitLoading}
                                key={`customBtn_${btnIndex}`}
                                onClick={button.onClick}
                                className={classes.reviewButton}
                            >
                                {button.label}
                                {submitLoading && (
                                    <Spinner animation="border" style={{ marginLeft: 8, width: 15, height: 15 }} />
                                )}
                            </button>
                        ))}
                    </>
                ) : (
                    <button onClick={handleReviewMore} className={classes.reviewButton}>
                        OKAY
                    </button>
                ))}
        </Modal>
    );
};

export default MultiSelectPopup;
